import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import UserBio from "./UserBio";
import BuyUnitsModal from "./BuyUnitsModal";
import SafetyActions from "./SafetyActions";
import "./Matches.css";

function MatchPhotoCarousel({ matchedUser, onPhotoClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedUrls, setFailedUrls] = useState({});

  function getInitial(username) {
    if (!username) {
      return "?";
    }

    return username.charAt(0).toUpperCase();
  }

  function getMatchedUserPhotoUrls(user) {
    if (!user) {
      return [];
    }

    const urls = [];

    if (Array.isArray(user.photoUrls)) {
      user.photoUrls.forEach((url) => {
        if (url && typeof url === "string" && url.trim() !== "") {
          urls.push(url);
        }
      });
    }

    if (user.mainPhotoUrl && user.mainPhotoUrl.trim() !== "") {
      urls.unshift(user.mainPhotoUrl);
    }

    if (user.photoUrl && user.photoUrl.trim() !== "") {
      urls.push(user.photoUrl);
    }

    const uniqueUrls = [];
    const seen = new Set();

    urls.forEach((url) => {
      if (!seen.has(url) && !failedUrls[url]) {
        seen.add(url);
        uniqueUrls.push(url);
      }
    });

    return uniqueUrls;
  }

  const photoUrls = getMatchedUserPhotoUrls(matchedUser);
  const hasPhotos = photoUrls.length > 0;
  const safeIndex = hasPhotos ? Math.min(currentIndex, photoUrls.length - 1) : 0;
  const currentPhotoUrl = hasPhotos ? photoUrls[safeIndex] : "";

  function goToPrevious(event) {
    event.preventDefault();
    event.stopPropagation();

    if (photoUrls.length <= 1) {
      return;
    }

    setCurrentIndex((previousIndex) => {
      if (previousIndex === 0) {
        return photoUrls.length - 1;
      }

      return previousIndex - 1;
    });
  }

  function goToNext(event) {
    event.preventDefault();
    event.stopPropagation();

    if (photoUrls.length <= 1) {
      return;
    }

    setCurrentIndex((previousIndex) => {
      if (previousIndex === photoUrls.length - 1) {
        return 0;
      }

      return previousIndex + 1;
    });
  }

  function handleImageError(url) {
    setFailedUrls((previousFailedUrls) => ({
      ...previousFailedUrls,
      [url]: true
    }));

    setCurrentIndex(0);
  }

  return (
    <div className="match-photo-carousel">
      {hasPhotos ? (
        <img
          src={currentPhotoUrl}
          alt={matchedUser.username || "Matched user"}
          className="match-photo-image"
          onError={() => handleImageError(currentPhotoUrl)}
          onClick={() => onPhotoClick?.(currentPhotoUrl, matchedUser.username)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onPhotoClick?.(currentPhotoUrl, matchedUser.username);
            }
          }}
        />
      ) : (
        <div className="match-photo-placeholder">
          <span>{getInitial(matchedUser.username)}</span>
        </div>
      )}

      <div className="match-photo-gradient"></div>

      {hasPhotos && (
        <span className="match-photo-zoom" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <line x1="15.5" y1="15.5" x2="21" y2="21" />
            <line x1="10.5" y1="8" x2="10.5" y2="13" />
            <line x1="8" y1="10.5" x2="13" y2="10.5" />
          </svg>
        </span>
      )}

      <div className="match-photo-badge">
        ♥ Match
      </div>

      {photoUrls.length > 1 && (
        <>
          <button
            type="button"
            className="match-carousel-button match-carousel-button-left"
            onClick={goToPrevious}
            aria-label="Previous photo"
          >
            ‹
          </button>

          <button
            type="button"
            className="match-carousel-button match-carousel-button-right"
            onClick={goToNext}
            aria-label="Next photo"
          >
            ›
          </button>

          <div className="match-carousel-dots">
            {photoUrls.map((url, index) => (
              <button
                key={url}
                type="button"
                className={
                  index === safeIndex
                    ? "match-carousel-dot match-carousel-dot-active"
                    : "match-carousel-dot"
                }
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setCurrentIndex(index);
                }}
                aria-label={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Matches({ pageType = "matches" }) {
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refillOpen, setRefillOpen] = useState(false);
  const [confirmDeleteMatchId, setConfirmDeleteMatchId] = useState(null);
  const [deletingMatchId, setDeletingMatchId] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    if (!selectedPhoto) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedPhoto(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedPhoto]);

  const { token, logout } = useAuth();
  const { t } = useI18n();

  const isMessagesPage = pageType === "messages";

  const pageTitle = isMessagesPage ? t("messages.title") : t("matches.title");

  const pageSubtitle = isMessagesPage
    ? "Conversations with your matches."
    : t("matches.subtitle");

  const loadingText = isMessagesPage
    ? "Loading your conversations..."
    : "Loading your matches...";

  const loadingBodyText = isMessagesPage
    ? "Finding your conversations..."
    : "Finding your connections...";

  const emptyTitle = isMessagesPage
    ? "No conversations yet"
    : "No matches yet";

  const emptyText = isMessagesPage
    ? "Start a conversation from your matches."
    : "Browse users and create your first match.";

  useEffect(() => {
    async function fetchMatches() {
      if (!token) {
        setError("You must be logged in to view this page.");
        setLoading(false);
        return;
      }

      const requestUrl =
        pageType === "messages"
          ? "/api/matches/conversations"
          : "/api/matches";

      const shouldMarkMatchesSeen = pageType !== "messages";

      try {
        setLoading(true);
        setError("");

        const response = await axios.get(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        console.log(
          pageType === "messages" ? "Conversations:" : "Matches:",
          response.data
        );

        setMatches(Array.isArray(response.data) ? response.data : []);

        if (shouldMarkMatchesSeen) {
          try {
            await axios.post(
              "/api/matches/mark-seen",
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
          } catch (markSeenErr) {
            console.error("Mark matches seen error:", markSeenErr);
          }
        }
      } catch (err) {
        console.error("Matches/conversations error:", err);

        if (err.response && err.response.status === 401) {
          logout();
          setError("Session expired. Please login again.");
        } else if (err.response && err.response.data) {
          setError(
            typeof err.response.data === "string"
              ? err.response.data
              : "Failed to load this page."
          );
        } else if (err.message) {
          setError(`Failed to load this page: ${err.message}`);
        } else {
          setError("Failed to load this page.");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchMatches();
  }, [token, logout, pageType]);

  function formatDate(dateValue) {
    if (!dateValue) {
      return "Unknown date";
    }

    return new Date(dateValue).toLocaleString();
  }

  function isMatchedUserOnline(matchedUser) {
    return matchedUser?.online === true;
  }

  async function handleConfirmDeleteMatch(matchId) {
    try {
      setDeletingMatchId(matchId);

      await axios.delete(`/api/matches/${matchId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }

      });

      setMatches((previousMatches) =>
        previousMatches.filter((match) => match.matchId !== matchId)
      );
    } catch (err) {
      console.error("Delete match error:", err);

      if (err.response && err.response.status === 401) {
        logout();
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to remove this match. Please try again.");
      }
    } finally {
      setDeletingMatchId(null);
      setConfirmDeleteMatchId(null);
    }
  }

  function removeBlockedMatch(matchId) {
    setMatches((previousMatches) =>
      previousMatches.filter((match) => match.matchId !== matchId)
    );
  }

  if (loading) {
    return (
      <div className="matches-page">
        <div className="matches-header">
          <h1 className="matches-title">{pageTitle}</h1>
          <p className="matches-subtitle">{loadingText}</p>
        </div>

        <div className="matches-empty">
          <p>{loadingBodyText}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matches-page">
        <div className="matches-header">
          <h1 className="matches-title">{pageTitle}</h1>
          <p className="matches-subtitle">Something went wrong.</p>
        </div>

        <div className="matches-message matches-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="matches-page">
      <div className="matches-header">
        <h1 className="matches-title">{pageTitle}</h1>
        <p className="matches-subtitle">
          {pageSubtitle}
        </p>
      </div>

      {matches.length === 0 && (
        <div className="matches-empty">
          <h2>{emptyTitle}</h2>
          <p>{emptyText}</p>

          {isMessagesPage ? (
            <Link to="/matches" className="browse-link-button">
              View Matches
            </Link>
          ) : (
            <Link to="/browse" className="browse-link-button">
              Browse Users
            </Link>
          )}
        </div>
      )}

      {matches.length > 0 && (
        <div className="matches-grid">
          {matches.map((match) => {
            const matchedUser = match.matchedUser || {};
            const online = isMatchedUserOnline(matchedUser);

            return (
              <div key={match.matchId} className="match-card">
                {!isMessagesPage && (
                  <button
                    type="button"
                    className="match-delete-button"
                    title="Remove match"
                    aria-label="Remove match"
                    onClick={() => setConfirmDeleteMatchId(match.matchId)}
                  >
                    🗑
                  </button>
                )}

                {!isMessagesPage && confirmDeleteMatchId === match.matchId && (
                  <div className="match-delete-confirm-overlay">
                    <div className="match-delete-confirm-box">
                      <p>Delete this match?</p>
                      <div className="match-delete-confirm-actions">
                        <button
                          type="button"
                          className="match-delete-confirm-yes"
                          disabled={deletingMatchId === match.matchId}
                          onClick={() => handleConfirmDeleteMatch(match.matchId)}
                        >
                          {deletingMatchId === match.matchId ? "Deleting..." : "Yes"}
                        </button>
                        <button
                          type="button"
                          className="match-delete-confirm-no"
                          disabled={deletingMatchId === match.matchId}
                          onClick={() => setConfirmDeleteMatchId(null)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <MatchPhotoCarousel matchedUser={matchedUser} onPhotoClick={(url, alt) => setSelectedPhoto({ url, alt: alt || "Matched user" })} />

                <div className="match-card-body">
                  <div className="match-name-row">
                    <h2 className="match-name">
                      {matchedUser.username || "Unknown user"}
                    </h2>

                    <span
                      className={
                        online
                          ? "profile-status-badge online"
                          : "profile-status-badge offline"
                      }
                      title={online ? "Online" : "Offline"}
                      aria-label={online ? "Online" : "Offline"}
                    >
                      <span className="profile-status-dot"></span>
                      <span>{online ? "Online" : "Offline"}</span>
                    </span>
                    <SafetyActions
                      userId={matchedUser.id || matchedUser.userId || matchedUser._id}
                      compact
                      onBlocked={() => removeBlockedMatch(match.matchId)}
                      onReported={() => removeBlockedMatch(match.matchId)}
                    />
                  </div>

                  <p
                    className="match-meta"
                    title={[
                      matchedUser.age !== undefined && matchedUser.age !== null
                        ? `${matchedUser.age} years`
                        : null,
                      matchedUser.gender,
                      matchedUser.city,
                      matchedUser.country
                    ].filter(Boolean).join(" • ")}
                  >
                    {[
                      matchedUser.age !== undefined && matchedUser.age !== null
                        ? `${matchedUser.age} years`
                        : null,
                      matchedUser.gender,
                      matchedUser.city,
                      matchedUser.country
                    ].filter(Boolean).join(" • ") || "Profile details not provided"}
                  </p>

                  <UserBio
                    bio={matchedUser.bio}
                    username={matchedUser.username}
                  />

                  <p className="match-date">
                    Matched at {formatDate(match.createdAt)}
                  </p>

                  <Link
                    to={`/messages/${match.matchId}`}
                    state={{ matchedUser: matchedUser }}
                    className="message-link-button"
                  >
                    {isMessagesPage ? "Open Chat" : t("matches.sendMessage")}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedPhoto && (
        <div
          className="match-photo-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedPhoto(null);
            }
          }}
        >
          <div className="match-photo-modal" role="dialog" aria-modal="true" aria-label={selectedPhoto.alt}>
            <button
              type="button"
              className="match-photo-modal-close"
              aria-label="Close photo"
              onClick={() => setSelectedPhoto(null)}
            >
              ×
            </button>
            <img src={selectedPhoto.url} alt={selectedPhoto.alt} />
          </div>
        </div>
      )}
      {!isMessagesPage && <BuyUnitsModal isOpen={refillOpen} initialPackCode="MATCH_REFILL" onClose={() => setRefillOpen(false)} />}
    </div>
  );
}

export default Matches;
